import { ICDSOptions } from '../interfaces/icdsoptions';
import { ICDSConfig } from '../interfaces/icdsconfig';
import { ICDSDependencies } from '../interfaces/icdsdependencies';
import { BehaviorSubject } from 'rxjs';
import { Status } from '../enums/status';
import { ParamsType } from '../interfaces/icds';
import { coreVendors } from '@nexxe/core-vendors';

export const cache_control = { 'Cache-Control': 'max-age=3600' };

// @dynamic
export class CdsMetadata {

	private static _cdsDataComposer: any;
	public static get cdsDataComposer(): any {
		return CdsMetadata._cdsDataComposer;
	}
	public static set cdsDataComposer(value: any) {
		CdsMetadata._cdsDataComposer = value;
	}

	private _endpoint: string;
	private _method: string;
	private _dependencies?: ICDSDependencies[];
	private _cache?: boolean;
	private _expireAge?: number;
	private _retry?: number;
	private _externalUrl: string;
	private _overridedInterceptor?: Object;
	private _extraHeaders?: Object;
	private _autoInvoke?: number;
	private _baseUrl: string;
	private _header: Object;
	private _baseInterceptor: Object;
	private _queryParams: Object;
	private _requestQueryParams: Object;
	private _outputPath: string;
	lastSuccess: number;
	status: Status;
	expireAge: number;
	observable: BehaviorSubject<any>;
	private _payload: Object;
	private _requestPayload: Object;
	key: string;
	withCredentials: boolean;
	id: string;
	private _urlTemplate: Object;
	private _responseType: string
	constructor(key, options: ICDSOptions, config: ICDSConfig, id: string, params: any, payload: any, urlTempate: Object) {
		this.id = id;
		this.key = key
		this._autoInvoke = options.autoInvoke;
		this._cache = options.cache;
		this._endpoint = this.resolveParamsInString(options.endpoint);
		this._dependencies = options.dependencies;
		this._expireAge = options.expireAge ? options.expireAge : 0;
		this._externalUrl = this.resolveParamsInString(options.externalUrl);
		this._overridedInterceptor = options.overridedInterceptor;
		this._extraHeaders = this.resolveParamsOrPayload(coreVendors.lodash.cloneDeep(options.extraHeaders));
		this._method = options.method;
		this.status = options.status ? options.status : Status.notUsed;
		this._retry = options.retry;
		this.lastSuccess = options.lastSuccess ? options.lastSuccess : 0;
		this.expireAge = options.expireAge ? options.expireAge : 1;
		this._baseUrl = this.resolveParamsInString(config.baseUrl);
		this._header = { ... this.resolveParamsOrPayload(coreVendors.lodash.cloneDeep(config.header)) };
		this._baseInterceptor = config.baseInterceptor;
		this._queryParams = this.resolveParamsOrPayload(coreVendors.lodash.cloneDeep(options.queryParams));
		this._requestQueryParams = this.resolveParamsOrPayload(params);
		this._payload = this.resolveParamsOrPayload(coreVendors.lodash.cloneDeep(options.payload));
		this._requestPayload = this.resolveParamsOrPayload(payload);
		this.withCredentials = options.withCredentials ? options.withCredentials : false;
		this._urlTemplate = urlTempate;
		this._outputPath = options.outputPath
		this._responseType = options.responseType
	}

	private resolveParamsOrPayload(paramsOrPayload: Object): Object {
		return CdsMetadata.cdsDataComposer.resolveParams(paramsOrPayload);
	}

	private resolveParamsInString(url: string): string {
		return CdsMetadata.cdsDataComposer.resolveParamsInString(url);
	}


	get responseType() {
		return this._responseType;
	}

	get outputPath() {
		return this._outputPath;
	}


	get method() {
		return this._method;
	}

	get options() {
		return {
			retry: this._retry,
			expireAge: this._expireAge,
			autoInvoke: this._autoInvoke
		};
	}

	get endpoint() {
		const url = (!this._externalUrl) ? this._baseUrl + this._endpoint : this._externalUrl;
		return getBacktickString(url, this._urlTemplate);
	}

	get headers() {
		var header;
		if (this._extraHeaders) header = Object.assign(this._header, this._extraHeaders)
		if (this._cache) header = Object.assign(this._header, cache_control);
		if (this._overridedInterceptor) header = Object.assign(this._header, this._overridedInterceptor);
		else header = Object.assign(this._header, this._baseInterceptor);
		//Header values can only be string type and any boolean/numeric values will be removed from the headers.
		//converting the numeric or boolean values to string will help retain the values in the headers. 
		for (let prop in header) {
			if ((header[prop] !== undefined || header[prop] !== null) && typeof header[prop] !== 'string') {
				header[prop] = header[prop].toString();
			}
		}
		return header;
	}

	get dependencies() {
		return this._dependencies
	}

	get queryParams() {
		return { ...this._queryParams, ...this._requestQueryParams };
	}

	get payload() {
		return formatParamsOrpayload(this._payload, this._requestPayload);
	}



	get isExpired(): boolean {
		const expireTime = this.lastSuccess + (this.expireAge * 60000);
		const currentTime: Number = (new Date()).getTime() / 60000;
		return (this.lastSuccess == 0 || (this.expireAge > 0 && currentTime > expireTime)) ? true : false;
	}


}

function formatParamsOrpayload(payload: ParamsType, requestPayload: Object) {
	if (requestPayload instanceof FormData) return requestPayload;
	if (requestPayload && (Array.isArray(requestPayload) || typeof requestPayload != 'object')) return requestPayload;
	if (payload && (Array.isArray(payload) || typeof payload != 'object')) return payload;
	if (payload && requestPayload) {
		return { ...(payload as any), ...requestPayload };
	} else if (requestPayload) return requestPayload;
	return payload;
}

// this is dangerous method and may cause a security breach and we will replace in the next release.
function getBacktickString(str: string, obj: any) {
	if (obj) {
		const matches = str.match(/\${.*}/g);
		let result = str;
		matches && matches.forEach(match => {
			let val: string = match.substring(2, match.length - 1);
			result = (val !== undefined && val !== null) ? result.replace(match, coreVendors.lodash.get(obj, val)) : result.replace(match, ' ');
		})
		str.replace(/\${/g, "${obj.")
		return result;
	} return str
}
